import { useEffect, useState } from 'react';
import { FolderOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileUploadField } from '../../components/FileUploadField';
import { usePortalDestination } from '../PortalDestinationContext';
import { LabelChips } from '../../care/DocumentLabels';

const slug = (l) => l.toLowerCase().replace(/\s+/g, '-');

export function PortalDocumentsCard() {
  const { api } = usePortalDestination();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [labels, setLabels] = useState([]);
  const [label, setLabel] = useState('none');

  useEffect(() => {
    let live = true;
    api.get('/documents')
      .then((r) => { if (live) setFiles(r.data.files || []); })
      .catch((e) => { if (live) setError(e?.response?.data?.detail || 'Could not load your documents.'); });
    api.get('/document-labels').then((r) => { if (live) setLabels(r.data.labels || []); }).catch(() => {});
    return () => { live = false; };
  }, [api]);

  const onChange = (next) => {
    if (next.length > files.length) setLabel('none');
    setFiles(next);
  };

  return (
    <Card data-testid="portal-documents-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FolderOpen className="w-4 h-4" /> My documents</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Share lab results, referral letters, insurance cards or old prescriptions with your pharmacy.
        </p>
        {error && <p role="alert" className="text-xs text-red-700" data-testid="portal-documents-error">{error}</p>}
        {labels.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">What is it? (optional)</span>
            <Select value={label} onValueChange={setLabel}>
              <SelectTrigger className="h-9 w-48" data-testid="portal-documents-label-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="portal-documents-label-none">No label</SelectItem>
                {labels.map((l) => (
                  <SelectItem key={l} value={l} data-testid={`portal-documents-label-${slug(l)}`}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <FileUploadField
          files={files}
          onChange={onChange}
          category="patient_document"
          portalClient={api}
          deleteOnRemove
          max={20}
          label=""
          testId="portal-documents"
          extraFields={{ label: label === 'none' ? '' : label }}
          renderExtra={(f, i) => (f.labels?.length ? <LabelChips labels={f.labels} testId={`portal-documents-labels-${i}`} /> : null)}
        />
      </CardContent>
    </Card>
  );
}
